// src/pages/Submissions.js
import React, { useEffect, useState, useCallback, useRef } from 'react';
import FilterDrawer from '../components/FilterDrawer';
import StatusBadge from '../components/StatusBadge';
import DetailModal from '../components/DetailModal';
import Pagination from '../components/Pagination';
import Table from '../components/Table';
import { FilePdfIcon, InboxIcon, EyeIcon, AlertTriangleIcon, FunnelIcon, SearchIcon, DownloadIcon, UploadIcon, WhatsAppIcon, ChevronDownIcon } from '../components/Icon';
import { api } from '../utils/api';
import { exportSingleToPDF, exportToExcel, parseStatusImport } from '../utils/exportUtils';
import { can } from '../utils/permissions';
import { getCurrentUser } from '../utils/auth';
import { confirmDialog, alertDialog } from '../utils/dialog';

const DEFAULT_FILTERS = { from: '', to: '', status: '', bhk: '', code_name: '', search: '', page: 1, limit: 20 };
const EXPORT_STATUSES = ['Pending', 'Reviewed', 'Approved', 'Rejected'];
const ALL_EXPORT_STATUSES = { Pending: true, Reviewed: true, Approved: true, Rejected: true };

export default function Submissions() {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [loading, setLoading]   = useState(true);
  const [apiError, setApiError] = useState('');
  const [filters, setFilters]   = useState(DEFAULT_FILTERS);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  const [pdfId, setPdfId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [codeNames, setCodeNames] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [waSendingId, setWaSendingId] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportStatuses, setExportStatuses] = useState(ALL_EXPORT_STATUSES);
  const [waBulkOpen, setWaBulkOpen] = useState(false);
  const [waBulkStatuses, setWaBulkStatuses] = useState({});
  const [waBulkSending, setWaBulkSending] = useState(false);
  const [waCounts, setWaCounts] = useState(null);
  const fileInputRef = useRef(null);
  const exportRef = useRef(null);

  // Close the export dropdown when clicking outside of it.
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onDown = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [exportOpen]);

  // Load the active projects' code names for the filter dropdown
  useEffect(() => {
    api.projects().then(res => {
      if (res && res.projects) {
        setCodeNames(
          res.projects
            .filter(p => p.is_active === '1' || p.is_active === 1 || p.is_active === true)
            .map(p => p.code_name)
            .filter(Boolean)
        );
      }
    }).catch(() => {});
  }, []);

  // Debounce the quick-search box → push into filters.search (server-side, all columns)
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters(f => (f.search === searchText ? f : { ...f, search: searchText, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchText]);

  // How many filters are currently active (for the Filter button badge)
  const activeFilterCount = ['from', 'to', 'status', 'bhk', 'code_name', 'search'].filter(k => filters[k]).length;

  const fetchData = useCallback(async (f) => {
    setLoading(true);
    setApiError('');
    const params = {};
    if (f.from)   params.from   = f.from;
    if (f.to)     params.to     = f.to;
    if (f.status) params.status = f.status;
    if (f.bhk)    params.bhk    = f.bhk;
    if (f.code_name) params.code_name = f.code_name;
    if (f.search) params.search = f.search;
    params.page  = f.page;
    params.limit = f.limit;
    const res = await api.list(params);
    if (res?._encrypted) {
      setApiError('🔒 Data is encrypted — set your encryption key in Settings to view records.');
      setRows([]);
      setTotal(0);
    } else if (res?.error) {
      setApiError(res.error);
      setRows([]);
      setTotal(0);
    } else {
      setRows(res.data || []);
      setTotal(res.total || 0);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(filters); }, [filters, fetchData]);

  const handleRowPDF = async (id) => {
    setPdfId(id);
    const detail = await api.detail(id);
    if (detail && !detail.error) {
      await exportSingleToPDF(detail);
    } else {
      alertDialog({ title: 'Export Failed', message: 'Could not load this record for PDF export.', tone: 'danger' });
    }
    setPdfId(null);
  };

  const handleStatusChange = (id, status) => {
    setRows(r => r.map(row => row.id === id ? { ...row, status } : row));
  };

  // ── Send the WhatsApp status-update template (Gupshup) for one record ──
  const handleSendWhatsapp = async (r) => {
    const ok = await confirmDialog({
      title: 'Send WhatsApp Update',
      message:
        `Send the EOI status update to ${r.name1 || 'this applicant'} (${r.mobile1 || 'no number'}) on WhatsApp?\n\n` +
        `Project: ${r.code_name || '—'}\nEOI No: ${r.ref_number || '—'}\nStatus: ${r.status || '—'}`,
      confirmText: 'Send',
    });
    if (!ok) return;
    setWaSendingId(r.id);
    const res = await api.sendWhatsapp(r.id);
    setWaSendingId(null);
    const sent = res?.success;
    setRows(rs => rs.map(row => row.id === r.id ? { ...row, whatsapp_status: sent ? 'Sent' : 'Failed' } : row));
    if (sent) {
      await alertDialog({ title: 'WhatsApp Sent', message: 'The status update was sent successfully.', tone: 'success' });
    } else {
      await alertDialog({ title: 'Send Failed', message: res?.error || 'Could not send the WhatsApp message.', tone: 'danger' });
    }
  };

  // ── Export: fetch records matching the current filters → Excel ──
  // Only the statuses ticked in the Export dropdown are included. (The Excel
  // sheet carries the "Ref No" + "Status" columns used for re-import.)
  const handleExport = async () => {
    const chosen = EXPORT_STATUSES.filter(s => exportStatuses[s]);
    if (!chosen.length) {
      await alertDialog({ title: 'Select a status', message: 'Please tick at least one status to export.' });
      return;
    }
    setExporting(true);
    try {
      const params = {};
      if (filters.from) params.from = filters.from;
      if (filters.to) params.to = filters.to;
      if (filters.bhk) params.bhk = filters.bhk;
      if (filters.code_name) params.code_name = filters.code_name;
      if (filters.search) params.search = filters.search;
      params.page = 1;
      params.limit = 100000; // pull everything that matches the filters
      const res = await api.list(params);
      let all = (res && res.data) || [];
      // Keep only the ticked statuses (skip filtering when all are selected).
      if (chosen.length < EXPORT_STATUSES.length) {
        const set = new Set(chosen);
        all = all.filter(r => set.has(r.status));
      }
      if (!all.length) { await alertDialog({ title: 'Nothing to Export', message: 'No records match the selected status(es) and filters.' }); return; }
      exportToExcel(all);
      setExportOpen(false);
    } catch {
      alertDialog({ title: 'Export Failed', message: 'Export failed. Please try again.', tone: 'danger' });
    } finally {
      setExporting(false);
    }
  };

  // ── Bulk WhatsApp: open the modal and load per-status counts ──
  const openWaBulk = async () => {
    setWaBulkStatuses({});
    setWaCounts(null);
    setWaBulkOpen(true);
    try {
      const res = await api.list({ page: 1, limit: 100000 });
      const data = (res && res.data) || [];
      const c = { Pending: 0, Reviewed: 0, Approved: 0, Rejected: 0 };
      data.forEach(r => { if (c[r.status] !== undefined) c[r.status]++; });
      setWaCounts(c);
    } catch { setWaCounts(null); }
  };

  // Send the WhatsApp status update to everyone in the selected statuses.
  const handleWaBulkSend = async () => {
    const chosen = EXPORT_STATUSES.filter(s => waBulkStatuses[s]);
    if (!chosen.length) {
      await alertDialog({ title: 'Select a status', message: 'Please select at least one status to send WhatsApp.' });
      return;
    }
    const targets = waCounts ? chosen.reduce((n, s) => n + (waCounts[s] || 0), 0) : null;
    const ok = await confirmDialog({
      title: 'Send WhatsApp Updates',
      message: `Send the status-update WhatsApp to applicants with status: ${chosen.join(', ')}?` +
        (targets != null ? `\n\nThis will message about ${targets} applicant(s).` : ''),
      confirmText: 'Send',
    });
    if (!ok) return;
    setWaBulkSending(true);
    const res = await api.sendWhatsappBulk(chosen);
    setWaBulkSending(false);
    if (res?.success) {
      setWaBulkOpen(false);
      await alertDialog({
        title: 'WhatsApp Sent',
        message: `Sent: ${res.sent}\nFailed: ${res.failed}\nSkipped (no mobile): ${res.skipped}\nTotal matched: ${res.total}`,
        tone: 'success',
      });
      fetchData(filters);
    } else {
      await alertDialog({ title: 'Send Failed', message: res?.error || 'Could not send the WhatsApp messages.', tone: 'danger' });
    }
  };

  // ── Import: read the edited Excel → bulk-update statuses by Ref No ──
  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setImporting(true);
    try {
      const items = await parseStatusImport(file);
      if (!items.length) {
        await alertDialog({ title: 'Nothing to Import', message: 'No valid "Ref No" + "Status" rows found in the file.', tone: 'danger' });
        return;
      }
      const ok = await confirmDialog({
        title: 'Import Statuses',
        message: `Update status for ${items.length} record(s) from this file?`,
        confirmText: 'Import',
      });
      if (!ok) return;

      const res = await api.statusBulk(items);
      if (res?.error || res?.success === false) {
        await alertDialog({ title: 'Import Failed', message: res.error || 'Unknown error', tone: 'danger' });
        return;
      }
      await alertDialog({
        title: 'Import Complete',
        message:
          `Updated: ${res.updated}\n` +
          `Already up-to-date: ${res.unchanged}\n` +
          `Ref No not found: ${res.notFound}\n` +
          `Skipped (invalid status): ${res.skipped}`,
        tone: 'success',
      });
      fetchData(filters); // refresh the table with new statuses
    } catch (err) {
      await alertDialog({ title: 'Import Failed', message: 'Could not import the file: ' + (err.message || 'invalid file'), tone: 'danger' });
    } finally {
      setImporting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };

  const allSelected = rows.length > 0 && rows.every(r => selected.has(r.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const dash = <span className="tbl-dash">—</span>;

  // WhatsApp delivery badge: Sent (green) / Failed (red) / Not Sent (grey).
  const waBadge = (s) => {
    const variant = { Sent: 'approved', Failed: 'rejected' }[s] || 'default';
    return <span className={`badge badge--${variant}`}>{s || 'Not Sent'}</span>;
  };

  const currentUser = getCurrentUser();
  const canExport = can(currentUser, 'submissions.export');
  const canStatus = can(currentUser, 'submissions.status');

  const columns = [
    {
      key: 'select',
      width: 36,
      header: () => <input type="checkbox" className="tbl-cell--clickable" checked={allSelected} onChange={toggleAll} />,
      render: (r) => <input type="checkbox" className="tbl-cell--clickable" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} />,
    },
    { key: 'code_name', header: 'Code Name', nowrap: true, onClick: (r) => setSelectedId(r.id),
      tdStyle: { fontWeight: 700, color: '#1a1a1a' }, render: (r) => r.code_name || dash },
    { key: 'ref_number', header: 'Ref No', nowrap: true, onClick: (r) => setSelectedId(r.id),
      tdStyle: { color: '#b8862e', fontWeight: 800, fontFamily: 'Georgia,serif' }, render: (r) => r.ref_number },
    { key: 'date', header: 'Date', nowrap: true, tdStyle: { fontSize: 11.5, color: '#aaa' },
      render: (r) => r.created_at?.split(' ')[0] },
    { key: 'name1', header: 'Applicant Name', onClick: (r) => setSelectedId(r.id),
      tdStyle: { fontWeight: 600 }, render: (r) => r.name1 },
    { key: 'mobile1', header: 'Mobile', render: (r) => r.mobile1 },
    { key: 'bhk', header: 'BHK', align: 'center', render: (r) =>
      r.project_type === 'Apartment' && r.bhk_config
        ? <span className="badge badge--pending">{r.bhk_config}</span>
        : dash },
    { key: 'facing', header: 'Facing', tdStyle: { fontSize: 12, color: '#555' }, render: (r) => r.facing || dash },
    { key: 'price_range', header: 'Price Range', tdStyle: { fontSize: 12, color: '#555' }, render: (r) => r.price_range },
    { key: 'location', header: 'Location', tdStyle: { fontSize: 12 }, render: (r) => r.location },
    { key: 'source_type', header: 'Source', tdStyle: { fontSize: 12, textTransform: 'capitalize' }, render: (r) => r.source_type },
    { key: 'source_name', header: 'Source Name', tdStyle: { fontSize: 12 }, render: (r) => r.source_name || dash },
    { key: 'status', header: 'Status', align: 'center', render: (r) => <StatusBadge status={r.status} /> },
    { key: 'whatsapp_status', header: 'WhatsApp', align: 'center', render: (r) => waBadge(r.whatsapp_status) },
    { key: 'actions', header: 'Actions', nowrap: true, render: (r) => (
      <>
        <button className="subs-action" onClick={() => setSelectedId(r.id)} title="View Details" aria-label="View Details"><EyeIcon size={14} /></button>
        {canStatus && (
          <button className="subs-action subs-action--wa" onClick={() => handleSendWhatsapp(r)} disabled={waSendingId === r.id} title="Send WhatsApp status update" aria-label="Send WhatsApp status update">{waSendingId === r.id ? '…' : <WhatsAppIcon size={14} />}</button>
        )}
        {canExport && (
          <button className="subs-action" onClick={() => handleRowPDF(r.id)} disabled={pdfId === r.id} title="Download PDF" aria-label="Download PDF">{pdfId === r.id ? '…' : <FilePdfIcon size={14} />}</button>
        )}
      </>
    ) },
  ];

  return (
    <div className="page">
      {/* Page header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">EOI Submissions</h1>
          <div className="page-sub">{total} total records</div>
        </div>
        <div className="subs-btn-group">
          <div className="subs-search">
            <SearchIcon size={15} />
            <input
              type="text"
              className="subs-search-input"
              placeholder="Search all columns…"
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
            />
            {searchText && (
              <button className="subs-search-clear" onClick={() => setSearchText('')} title="Clear search" aria-label="Clear search">×</button>
            )}
          </div>
          {canExport && (
            <div className="subs-export-wrap" ref={exportRef}>
              <button className="subs-filter-btn" onClick={() => setExportOpen(o => !o)} disabled={exporting} title="Export submissions to Excel">
                <DownloadIcon size={15} /> <span>{exporting ? 'Exporting…' : 'Export'}</span>
                <ChevronDownIcon size={13} />
              </button>
              {exportOpen && (
                <div className="subs-export-menu">
                  <div className="subs-export-menu-title">Export by status</div>
                  {EXPORT_STATUSES.map(s => (
                    <label key={s} className="subs-export-opt">
                      <input
                        type="checkbox"
                        checked={!!exportStatuses[s]}
                        onChange={() => setExportStatuses(p => ({ ...p, [s]: !p[s] }))}
                      />
                      <span>{s}</span>
                    </label>
                  ))}
                  <div className="subs-export-foot">
                    <button className="subs-export-link" onClick={() => setExportStatuses(ALL_EXPORT_STATUSES)}>All</button>
                    <button className="subs-export-link" onClick={() => setExportStatuses({})}>None</button>
                    <button className="subs-export-go" onClick={handleExport} disabled={exporting}>
                      {exporting ? 'Exporting…' : 'Export'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {canStatus && (
            <button className="subs-filter-btn subs-wa-btn" onClick={openWaBulk} title="Send WhatsApp status update to applicants by status">
              <WhatsAppIcon size={15} /> <span>WhatsApp</span>
            </button>
          )}
          {canStatus && (
            <>
              <button className="subs-filter-btn" onClick={() => fileInputRef.current?.click()} disabled={importing} title="Import Excel to bulk-update statuses">
                <UploadIcon size={15} /> <span>{importing ? 'Importing…' : 'Import'}</span>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: 'none' }}
                onChange={handleImportFile}
              />
            </>
          )}
          <button className="subs-filter-btn" onClick={() => setFilterOpen(true)}>
            <FunnelIcon size={15} /> <span>Filter</span>
            {activeFilterCount > 0 && <span className="subs-filter-badge">{activeFilterCount}</span>}
          </button>
        </div>
      </div>

      {/* Filters (off-canvas drawer) */}
      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        filters={filters}
        codeNames={codeNames}
        onChange={setFilters}
        onReset={() => setFilters(DEFAULT_FILTERS)}
      />

      {/* API error banner */}
      {apiError && !loading && (
        <div className="subs-error-banner">
          <div className="subs-error-title">
            <AlertTriangleIcon size={16} /> Cannot load submissions
          </div>
          <div className="subs-error-detail">{apiError}</div>
        </div>
      )}

      {/* Table */}
      <div className="table-card">
        {loading ? (
          <div className="loader-wrap"><div className="spinner" /></div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <div className={apiError ? 'subs-empty-icon--error' : 'subs-empty-icon'}>
              {apiError ? <AlertTriangleIcon size={42} /> : <InboxIcon size={42} />}
            </div>
            <div className="subs-empty-title">
              {apiError ? 'Could not load data' : 'No records found'}
            </div>
            <div className="subs-empty-sub">
              {apiError ? 'See error above for details' : 'Try adjusting your filters'}
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={rows}
            rowKey={(r) => r.id}
            minWidth={1380}
            maxHeight="62vh"
            rowStyle={(r) => (selected.has(r.id) ? { background: '#fdf6e3' } : null)}
          />
        )}

        {/* Pagination — kept inside the same card as the table (like the Dashboard) */}
        {!loading && !apiError && rows.length > 0 && (
          <Pagination
            page={filters.page}
            pageSize={filters.limit}
            total={total}
            onPageChange={p => setFilters(f => ({ ...f, page: p }))}
            onPageSizeChange={n => setFilters(f => ({ ...f, limit: n, page: 1 }))}
            pageSizeOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* Detail Modal */}
      {selectedId && (
        <DetailModal
          id={selectedId}
          onClose={() => setSelectedId(null)}
          onStatusChange={handleStatusChange}
        />
      )}

      {/* Bulk WhatsApp modal — pick statuses → notify those applicants */}
      {waBulkOpen && (
        <div className="wa-modal-overlay" onClick={e => e.target === e.currentTarget && !waBulkSending && setWaBulkOpen(false)}>
          <div className="wa-modal">
            <div className="wa-modal-head">
              <span className="wa-modal-icon"><WhatsAppIcon size={20} /></span>
              <div className="wa-modal-heading">
                <div className="wa-modal-title">Send WhatsApp Update</div>
                <div className="wa-modal-sub">Select which status's applicants to notify</div>
              </div>
              <button className="wa-modal-close" onClick={() => !waBulkSending && setWaBulkOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="wa-modal-body">
              {EXPORT_STATUSES.map(s => (
                <label key={s} className="wa-modal-opt">
                  <input
                    type="checkbox"
                    checked={!!waBulkStatuses[s]}
                    disabled={waBulkSending}
                    onChange={() => setWaBulkStatuses(p => ({ ...p, [s]: !p[s] }))}
                  />
                  <span className="wa-modal-opt-label">{s}</span>
                  <span className="wa-modal-opt-count">{waCounts ? (waCounts[s] ?? 0) : '…'}</span>
                </label>
              ))}
            </div>
            <div className="wa-modal-foot">
              <button className="wa-modal-cancel" onClick={() => setWaBulkOpen(false)} disabled={waBulkSending}>Cancel</button>
              <button className="wa-modal-send" onClick={handleWaBulkSend} disabled={waBulkSending}>
                {waBulkSending ? 'Sending…' : 'Send WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
